import React, { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { INTRO, focusIn } from '../utils/reveal';
import { useIsMobile } from '../hooks/useIsMobile';
import { madness } from '../utils/madness';
import { quest } from '../utils/quest';
import { runPixelAction } from '../utils/pixelActions';
import { parentCompany } from '../data/parentCompany';
import { QuestDragReveal } from './QuestDragReveal';

/** Cursor within this distance (px) of CONTACT counts as "getting close" for Pixel's anger */
const PROXIMITY_RANGE = 520;

/** Top-right: a single black CONTACT pill. Hover: it fades very slightly. */
export function NavBar() {
  const reduce = useReducedMotion();
  const contactRef = useRef<HTMLAnchorElement>(null);
  const mobile = useIsMobile();

  // Tell Pixel how close the cursor is to CONTACT (he gets angrier the closer you tease it)
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const el = contactRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      madness.setContact((r.left + r.width / 2) / window.innerWidth * 2 - 1, -((r.top + r.height / 2) / window.innerHeight * 2 - 1));
      const dx = Math.max(r.left - e.clientX, 0, e.clientX - r.right);
      const dy = Math.max(r.top - e.clientY, 0, e.clientY - r.bottom);
      madness.setProximity(1 - Math.hypot(dx, dy) / PROXIMITY_RANGE);
    };
    window.addEventListener('pointermove', onMove, {
      passive: true
    });
    return () => window.removeEventListener('pointermove', onMove);
  }, []);
  return (
    // Mobile: centred at the bottom, ~34px above the socials (which sit 56px up, ~22px tall);
    // desktop: top-right corner
    <header className="absolute inset-x-0 bottom-[7rem] z-30 md:relative md:inset-x-auto md:bottom-auto" data-id="element-156" data-mp="16y7jace0atoo">
      <nav aria-label="Main" className="flex items-start justify-center px-3 md:justify-end md:px-[calc(1.6*var(--vw))] md:py-[calc(1.6*var(--vw))]" data-id="element-157" data-mp="9h0j9giu334q">
        <div className="flex items-center gap-3.5 md:gap-2" data-id="element-158" data-mp="1hrsk9fworojy">
        {/* Hover fade lives on the wrapper so it never fights the link's intro animation */}
        {/* Quest: shard one hides behind this button (drag it aside) */}
        <QuestDragReveal spot="button" data-id="element-159" data-mp="1qkccnymdwwwc">
        <div data-drone-push className="transition-opacity duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] focus-within:opacity-[0.7] [@media(hover:hover)]:hover:opacity-[0.7] motion-reduce:transition-none" data-id="element-160" data-mp="1qjaxpj28zq9c">
          <motion.a ref={contactRef} href="#contact"
              // No teasing Pixel into a rage while he's busy with the quest
              onPointerEnter={() => {
                if (!quest.isActive()) madness.enter();
              }} onPointerLeave={madness.leave}
              // Glides down to the contact footer (same smooth scroll as Pixel's guided trips)
              onClick={e => {
                e.preventDefault();
                madness.click();
                runPixelAction({
                  type: 'scroll',
                  target: '#contact'
                });
              }} className="inline-flex h-[3.25rem] items-center whitespace-nowrap rounded-full bg-black px-8 text-[1.125rem] md:h-[clamp(2.5rem,calc(3.2*var(--vw)),3rem)] md:px-[clamp(1.25rem,calc(1.8*var(--vw)),1.75rem)] md:text-[clamp(0.875rem,calc(1.15*var(--vw)),1.05rem)] font-semibold leading-[1.2] tracking-[-0.03em] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black" key={mobile ? 'm' : 'd'} {...mobile ? {} : focusIn(INTRO.links, reduce, {
                y: 6,
                blur: 6,
                duration: 1.1
              })} data-id="element-161" data-mp="tu3eihp3pt8l">
            Contact us
          </motion.a>
        </div>
        </QuestDragReveal>
        {/* Secondary: our parent company — same size, outlined so "Contact us" stays the main action */}
        <div data-drone-push className="transition-opacity duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] focus-within:opacity-[0.7] [@media(hover:hover)]:hover:opacity-[0.7] motion-reduce:transition-none" data-id="element-162" data-mp="3oegglamw21hn">
          <motion.a href={parentCompany.href} target="_blank" rel="noopener noreferrer" className="inline-flex h-[3.25rem] items-center whitespace-nowrap rounded-full border-2 border-black px-8 text-[1.125rem] md:h-[clamp(2.5rem,calc(3.2*var(--vw)),3rem)] md:px-[clamp(1.25rem,calc(1.8*var(--vw)),1.75rem)] md:text-[clamp(0.875rem,calc(1.15*var(--vw)),1.05rem)] font-semibold leading-[1.2] tracking-[-0.03em] text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black" key={mobile ? 'm' : 'd'} {...mobile ? {} : focusIn(INTRO.links + INTRO.link, reduce, {
              y: 6,
              blur: 6,
              duration: 1.1
            })} data-id="element-163" data-mp="24ilutxf7nmjf">
            {parentCompany.label}
          </motion.a>
        </div>
        </div>
      </nav>
    </header>
  );
}